import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { Lead, nextStepUrl, STARTER } from '../../config/offer';

type LeadFormApi = { open: (source: string) => void };
const LeadFormContext = createContext<LeadFormApi>({ open: () => {} });
export const useLeadForm = () => useContext(LeadFormContext);

const reasons = ['Tarot guidance', 'Chakra & energy healing', 'Not sure yet'];
const genders = ['Female', 'Male', 'Other', 'Prefer not to say'];

const inputClass =
  'w-full rounded-xl border border-lavender-300 bg-white px-4 py-3 text-plum placeholder:text-plum-soft/60 focus:outline-none focus:ring-2 focus:ring-lavender-500';

/** Sends the details to /api/lead (saved to the Google Sheet) without holding up the visitor. */
function saveLead(payload: Record<string, string>) {
  const body = JSON.stringify(payload);
  try {
    if (navigator.sendBeacon?.('/api/lead', new Blob([body], { type: 'application/json' }))) return;
  } catch {
    // fall through to fetch
  }
  fetch('/api/lead', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
}

/** Short "let's begin" form in a native dialog. Opened by any "Experience the difference" button. */
export const LeadFormProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [source, setSource] = useState('site');
  const [error, setError] = useState('');

  const open = useCallback((src: string) => {
    setSource(src);
    setError('');
    dialogRef.current?.showModal();
  }, []);

  const close = () => dialogRef.current?.close();

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const lead: Lead = {
      name: String(data.get('name') || '').trim(),
      whatsapp: String(data.get('whatsapp') || '').replace(/[\s-]/g, ''),
      gender: String(data.get('gender') || ''),
      reason: String(data.get('reason') || ''),
      intent: String(data.get('intent') || '').trim(),
    };
    if (!lead.reason || !lead.name || !lead.gender) {
      setError('Please fill in every field.');
      return;
    }
    if (!/^(\+?91)?[6-9]\d{9}$/.test(lead.whatsapp)) {
      setError('Please enter a valid 10-digit WhatsApp number.');
      return;
    }
    saveLead({ ...lead, source, company: String(data.get('company') || '') });
    window.open(nextStepUrl(lead, source), '_blank', 'noopener');
    close();
  };

  return (
    <LeadFormContext.Provider value={{ open }}>
      {children}
      <dialog
        ref={dialogRef}
        aria-labelledby="lead-form-title"
        data-testid="lead-form"
        className="w-[min(92vw,30rem)] rounded-3xl bg-cream p-0 text-plum shadow-xl backdrop:bg-plum/40 backdrop:backdrop-blur-sm"
      >
        <form onSubmit={onSubmit} noValidate className="p-6 sm:p-8 space-y-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 id="lead-form-title" className="font-fraunces text-2xl font-medium">Let&rsquo;s begin</h2>
              <p className="text-sm text-plum-soft mt-1">A few details so Madhulika can prepare for your call.</p>
            </div>
            <button type="button" onClick={close} aria-label="Close" className="p-1 text-plum-soft hover:text-plum">
              <X className="w-5 h-5" />
            </button>
          </div>

          <fieldset>
            <legend className="text-sm font-bold mb-2">What brings you here?</legend>
            <div className="flex flex-wrap gap-2">
              {reasons.map((r) => (
                <label key={r} className="cursor-pointer">
                  <input type="radio" name="reason" value={r} className="peer sr-only" />
                  <span className="inline-block rounded-full border border-lavender-300 px-4 py-2 text-sm text-plum-soft peer-checked:bg-lavender-700 peer-checked:border-lavender-700 peer-checked:text-white peer-focus-visible:ring-2 peer-focus-visible:ring-lavender-500 transition-colors">
                    {r}
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          <label className="block">
            <span className="text-sm font-bold">What would you like from this session?</span>{' '}
            <span className="text-xs text-plum-soft">(optional)</span>
            <input
              name="intent"
              maxLength={140}
              autoComplete="off"
              placeholder="e.g. clarity on a career change"
              className={`${inputClass} mt-1`}
            />
            <span className="block text-xs text-plum-soft mt-1">One line is enough. Madhulika uses it to personalise your PDF.</span>
          </label>

          <label className="block">
            <span className="text-sm font-bold">Your name</span>
            <input name="name" autoComplete="name" className={`${inputClass} mt-1`} />
          </label>

          <label className="block">
            <span className="text-sm font-bold">WhatsApp number</span>
            <input name="whatsapp" type="tel" inputMode="tel" autoComplete="tel" placeholder="98765 43210" className={`${inputClass} mt-1`} />
          </label>

          <label className="block">
            <span className="text-sm font-bold">Gender</span>
            <span className="relative block mt-1">
              <select name="gender" defaultValue="" className={`${inputClass} appearance-none pr-10`}>
                <option value="" disabled>Select</option>
                {genders.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
              <ChevronDown className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-plum-soft" aria-hidden="true" />
            </span>
          </label>

          <input type="text" name="company" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />

          <p className="rounded-2xl bg-lavender-100 p-4 text-sm text-plum-soft leading-relaxed">
            <strong className="text-plum">Why {STARTER.price}?</strong> We ask a small {STARTER.price} token for your personalised PDF. It isn&rsquo;t about
            the money: it simply helps Madhulika give her time to people who are genuinely looking for guidance.
          </p>

          {error && <p role="alert" className="text-sm text-[#9F1239]">{error}</p>}

          <button type="submit" className="w-full rounded-full bg-lavender-700 px-6 py-3 font-bold text-white hover:bg-lavender-600 transition-colors">
            Continue
          </button>
          <p className="text-xs text-center text-plum-soft">
            After this you choose a time for your {STARTER.duration} call. Your details are only used to arrange your session.
          </p>
        </form>
      </dialog>
    </LeadFormContext.Provider>
  );
};
